import axios from 'axios';
import Cookies from 'universal-cookie';

const API_BASE = process.env.REACT_APP_API_BASE_URL;

function getToken() {
    const cookies = new Cookies();
    return cookies.get("token");
}

/**
 * Get all questionnaires for a project
 * @param {string} projectUuid - Project UUID
 * @param {object} params - Query parameters (status, search, per_page, etc.)
 * @returns {Promise} API response
 */
export const getQuestionnaires = async (projectUuid, params = {}) => {
    const response = await axios.get(`${API_BASE}/projects/${projectUuid}/questionnaires`, {
        headers: {
            authorization: `Bearer ${getToken()}`
        },
        params
    });
    return response.data;
};

/**
 * Get a specific questionnaire
 * @param {string} projectUuid - Project UUID
 * @param {string} questionnaireUuid - Questionnaire UUID
 * @returns {Promise} API response
 */
export const getQuestionnaire = async (projectUuid, questionnaireUuid) => {
    const response = await axios.get(`${API_BASE}/projects/${projectUuid}/questionnaires/${questionnaireUuid}`, {
        headers: {
            authorization: `Bearer ${getToken()}`
        }
    });
    return response.data;
};

/**
 * Create a new questionnaire
 * @param {string} projectUuid - Project UUID
 * @param {object} questionnaireData - Questionnaire data
 * @returns {Promise} API response
 */
export const createQuestionnaire = async (projectUuid, questionnaireData) => {
    const response = await axios.post(`${API_BASE}/projects/${projectUuid}/questionnaires`, questionnaireData, {
        headers: {
            authorization: `Bearer ${getToken()}`
        }
    });
    return response.data;
};

/**
 * Update a questionnaire
 * @param {string} projectUuid - Project UUID
 * @param {string} questionnaireUuid - Questionnaire UUID
 * @param {object} questionnaireData - Updated questionnaire data
 * @returns {Promise} API response
 */
export const updateQuestionnaire = async (projectUuid, questionnaireUuid, questionnaireData) => {
    const response = await axios.put(`${API_BASE}/projects/${projectUuid}/questionnaires/${questionnaireUuid}`, questionnaireData, {
        headers: {
            authorization: `Bearer ${getToken()}`
        }
    });
    return response.data;
};

/**
 * Delete a questionnaire
 * @param {string} projectUuid - Project UUID
 * @param {string} questionnaireUuid - Questionnaire UUID
 * @returns {Promise} API response
 */
export const deleteQuestionnaire = async (projectUuid, questionnaireUuid) => {
    const response = await axios.delete(`${API_BASE}/projects/${projectUuid}/questionnaires/${questionnaireUuid}`, {
        headers: {
            authorization: `Bearer ${getToken()}`
        }
    });
    return response.data;
};

/**
 * Duplicate a questionnaire
 * @param {string} projectUuid - Project UUID
 * @param {string} questionnaireUuid - Questionnaire UUID to duplicate
 * @returns {Promise} API response
 */
export const duplicateQuestionnaire = async (projectUuid, questionnaireUuid) => {
    const response = await axios.post(`${API_BASE}/projects/${projectUuid}/questionnaires/${questionnaireUuid}/duplicate`, {}, {
        headers: {
            authorization: `Bearer ${getToken()}`
        }
    });
    return response.data;
};

/**
 * Get questionnaire analytics
 * @param {string} projectUuid - Project UUID
 * @param {string} questionnaireUuid - Questionnaire UUID
 * @returns {Promise} API response
 */
export const getQuestionnaireAnalytics = async (projectUuid, questionnaireUuid) => {
    const response = await axios.get(`${API_BASE}/projects/${projectUuid}/questionnaires/${questionnaireUuid}/analytics`, {
        headers: {
            authorization: `Bearer ${getToken()}`
        }
    });
    return response.data;
};

/**
 * Get per-question aggregate gaze heatmap data for the analytics overview:
 * downsampled gaze points and a cached "middle frame" image per question.
 * @param {string} projectUuid - Project UUID
 * @param {string} questionnaireUuid - Questionnaire UUID
 * @returns {Promise} API response ({ questions: [...], frames_generating: bool })
 */
export const getQuestionnaireGazeOverview = async (projectUuid, questionnaireUuid) => {
    const response = await axios.get(`${API_BASE}/projects/${projectUuid}/questionnaires/${questionnaireUuid}/gaze-overview`, {
        headers: {
            authorization: `Bearer ${getToken()}`
        }
    });
    return response.data;
};

/**
 * Get per-question cognitive-affective constructs (cognitive load, decision
 * conflict, attention, valence, arousal) scored against per-question norms.
 * @param {string} projectUuid - Project UUID
 * @param {string} questionnaireUuid - Questionnaire UUID
 * @returns {Promise} API response ({ questions: [...] })
 */
export const getQuestionnaireConstructs = async (projectUuid, questionnaireUuid) => {
    const response = await axios.get(`${API_BASE}/projects/${projectUuid}/questionnaires/${questionnaireUuid}/constructs`, {
        headers: {
            authorization: `Bearer ${getToken()}`
        }
    });
    return response.data;
};

/**
 * Get the generated LLM research-insight narrative for a questionnaire.
 * @returns {Promise} ({ status, content, generated_at })
 */
export const getQuestionnaireInsights = async (projectUuid, questionnaireUuid) => {
    const response = await axios.get(`${API_BASE}/projects/${projectUuid}/questionnaires/${questionnaireUuid}/insights`, {
        headers: { authorization: `Bearer ${getToken()}` }
    });
    return response.data;
};

/**
 * Trigger (re)generation of the research-insight narrative (runs in background).
 * @returns {Promise} ({ status: 'generating' })
 */
export const generateQuestionnaireInsights = async (projectUuid, questionnaireUuid) => {
    const response = await axios.post(`${API_BASE}/projects/${projectUuid}/questionnaires/${questionnaireUuid}/insights/generate`, {}, {
        headers: { authorization: `Bearer ${getToken()}` }
    });
    return response.data;
};

/**
 * Get public questionnaire (no auth required)
 * @param {string} questionnaireUuid - Questionnaire UUID
 * @returns {Promise} API response
 */
export const getPublicQuestionnaire = async (questionnaireUuid) => {
    const response = await axios.get(`${API_BASE}/public/questionnaires/${questionnaireUuid}`);
    return response.data;
};

// Question management functions

/**
 * Create a new question in a questionnaire
 * @param {string} projectUuid - Project UUID
 * @param {string} questionnaireUuid - Questionnaire UUID
 * @param {object} questionData - Question data
 * @returns {Promise} API response
 */
export const createQuestion = async (projectUuid, questionnaireUuid, questionData) => {
    const response = await axios.post(`${API_BASE}/projects/${projectUuid}/questionnaires/${questionnaireUuid}/questions`, questionData, {
        headers: {
            authorization: `Bearer ${getToken()}`
        }
    });
    return response.data;
};

/**
 * Update a question
 * @param {string} projectUuid - Project UUID
 * @param {string} questionnaireUuid - Questionnaire UUID
 * @param {string} questionUuid - Question UUID
 * @param {object} questionData - Updated question data
 * @returns {Promise} API response
 */
export const updateQuestion = async (projectUuid, questionnaireUuid, questionUuid, questionData) => {
    try {
        const response = await axios.put(`${API_BASE}/projects/${projectUuid}/questionnaires/${questionnaireUuid}/questions/${questionUuid}`, questionData, {
            headers: {
                authorization: `Bearer ${getToken()}`
            }
        });
        return response.data;
    } catch (error) {
        console.error('updateQuestion API error:', {
            error: error.message,
            status: error.response?.status,
            data: error.response?.data
        });
        throw error;
    }
};

/**
 * Delete a question
 * @param {string} projectUuid - Project UUID
 * @param {string} questionnaireUuid - Questionnaire UUID
 * @param {string} questionUuid - Question UUID
 * @returns {Promise} API response
 */
export const deleteQuestion = async (projectUuid, questionnaireUuid, questionUuid) => {
    const response = await axios.delete(`${API_BASE}/projects/${projectUuid}/questionnaires/${questionnaireUuid}/questions/${questionUuid}`, {
        headers: {
            authorization: `Bearer ${getToken()}`
        }
    });
    return response.data;
};

/**
 * Reorder questions in a questionnaire
 * @param {string} projectUuid - Project UUID
 * @param {string} questionnaireUuid - Questionnaire UUID
 * @param {Array} questions - Array of questions with order
 * @returns {Promise} API response
 */
export const reorderQuestions = async (projectUuid, questionnaireUuid, questions) => {
    const response = await axios.put(`${API_BASE}/projects/${projectUuid}/questionnaires/${questionnaireUuid}/questions/reorder`, {
        questions
    }, {
        headers: {
            authorization: `Bearer ${getToken()}`
        }
    });
    return response.data;
};

/**
 * Update question options
 * @param {string} projectUuid - Project UUID
 * @param {string} questionnaireUuid - Questionnaire UUID
 * @param {string} questionUuid - Question UUID
 * @param {Array} options - Array of options
 * @returns {Promise} API response
 */
export const updateQuestionOptions = async (projectUuid, questionnaireUuid, questionUuid, options) => {
    const response = await axios.put(`${API_BASE}/projects/${projectUuid}/questionnaires/${questionnaireUuid}/questions/${questionUuid}/options`, {
        options
    }, {
        headers: {
            authorization: `Bearer ${getToken()}`
        }
    });
    return response.data;
};

/**
 * Upload question image
 * @param {string} projectUuid - Project UUID
 * @param {string} questionnaireUuid - Questionnaire UUID
 * @param {string} questionUuid - Question UUID
 * @param {File} imageFile - Image file
 * @param {string} collection - Image collection ('question_image' or 'background_image')
 * @returns {Promise} API response
 */
export const uploadQuestionImage = async (projectUuid, questionnaireUuid, questionUuid, imageFile, collection = 'question_image') => {
    const formData = new FormData();
    formData.append('image', imageFile);
    formData.append('collection', collection);

    const response = await axios.post(
        `${API_BASE}/projects/${projectUuid}/questionnaires/${questionnaireUuid}/questions/${questionUuid}/upload-image`,
        formData,
        {
            headers: {
                authorization: `Bearer ${getToken()}`,
                'Content-Type': 'multipart/form-data'
            }
        }
    );
    return response.data;
};

/**
 * Delete question image
 * @param {string} projectUuid - Project UUID
 * @param {string} questionnaireUuid - Questionnaire UUID
 * @param {string} questionUuid - Question UUID
 * @param {string} collection - Image collection ('question_image' or 'background_image')
 * @returns {Promise} API response
 */
export const deleteQuestionImage = async (projectUuid, questionnaireUuid, questionUuid, collection = 'question_image') => {
    const response = await axios.delete(`${API_BASE}/projects/${projectUuid}/questionnaires/${questionnaireUuid}/questions/${questionUuid}/delete-image`, {
        headers: {
            authorization: `Bearer ${getToken()}`
        },
        params: { collection }
    });
    return response.data;
};

/**
 * Upload question media (image or video)
 * @param {string} projectUuid - Project UUID
 * @param {string} questionnaireUuid - Questionnaire UUID
 * @param {string} questionUuid - Question UUID
 * @param {File} mediaFile - Media file (image or video)
 * @param {string} type - Media type ('image' or 'video')
 * @returns {Promise} API response with signed URLs
 */
export const uploadQuestionMedia = async (projectUuid, questionnaireUuid, questionUuid, mediaFile, type) => {
    const formData = new FormData();
    formData.append('media', mediaFile);
    formData.append('type', type);

    const uploadUrl = `${API_BASE}/projects/${projectUuid}/questionnaires/${questionnaireUuid}/questions/${questionUuid}/upload-media`;
    
    console.log('CORS Debug - API Request Details:', {
        url: uploadUrl,
        API_BASE,
        type,
        fileSize: mediaFile.size,
        fileName: mediaFile.name,
        projectUuid,
        questionnaireUuid,
        questionUuid,
        hasToken: !!getToken()
    });

    try {
        const response = await axios.post(uploadUrl, formData, {
            headers: {
                authorization: `Bearer ${getToken()}`,
                'Content-Type': 'multipart/form-data'
            }
        });
        return response.data;
    } catch (error) {
        console.error('CORS Debug - API Request Failed:', {
            error: error.message,
            code: error.code,
            status: error.response?.status,
            statusText: error.response?.statusText,
            data: error.response?.data,
            url: uploadUrl,
            isCorsError: error.message.toLowerCase().includes('cors') || 
                        error.code === 'ERR_BLOCKED_BY_CORS' ||
                        (error.response?.status === 0 && error.request)
        });
        throw error;
    }
};

/**
 * Delete question media (image or video)
 * @param {string} projectUuid - Project UUID
 * @param {string} questionnaireUuid - Questionnaire UUID
 * @param {string} questionUuid - Question UUID
 * @param {string} type - Media type ('image' or 'video')
 * @returns {Promise} API response
 */
export const deleteQuestionMedia = async (projectUuid, questionnaireUuid, questionUuid, type) => {
    const response = await axios.delete(`${API_BASE}/projects/${projectUuid}/questionnaires/${questionnaireUuid}/questions/${questionUuid}/delete-media`, {
        headers: {
            authorization: `Bearer ${getToken()}`
        },
        data: { type }
    });
    return response.data;
};

/**
 * Upload media for a question option
 * @param {string} projectUuid - Project UUID
 * @param {string} questionnaireUuid - Questionnaire UUID
 * @param {string} questionUuid - Question UUID
 * @param {string} optionId - Option ID
 * @param {File} mediaFile - Media file (image)
 * @returns {Promise} API response with signed URLs
 */
export const uploadOptionMedia = async (projectUuid, questionnaireUuid, questionUuid, optionId, mediaFile) => {
    const formData = new FormData();
    formData.append('media', mediaFile);

    const uploadUrl = `${API_BASE}/projects/${projectUuid}/questionnaires/${questionnaireUuid}/questions/${questionUuid}/options/${optionId}/upload-media`;
    
    console.log('Option Media Upload - API Request Details:', {
        url: uploadUrl,
        API_BASE,
        fileSize: mediaFile.size,
        fileName: mediaFile.name,
        projectUuid,
        questionnaireUuid,
        questionUuid,
        optionId,
        hasToken: !!getToken()
    });

    try {
        const response = await axios.post(uploadUrl, formData, {
            headers: {
                authorization: `Bearer ${getToken()}`,
                'Content-Type': 'multipart/form-data'
            }
        });
        return response.data;
    } catch (error) {
        console.error('Option Media Upload - API Request Failed:', {
            error: error.message,
            code: error.code,
            status: error.response?.status,
            statusText: error.response?.statusText,
            data: error.response?.data,
            url: uploadUrl
        });
        throw error;
    }
};

/**
 * Delete media for a question option
 * @param {string} projectUuid - Project UUID
 * @param {string} questionnaireUuid - Questionnaire UUID
 * @param {string} questionUuid - Question UUID
 * @param {string} optionId - Option ID
 * @returns {Promise} API response
 */
export const deleteOptionMedia = async (projectUuid, questionnaireUuid, questionUuid, optionId) => {
    const response = await axios.delete(`${API_BASE}/projects/${projectUuid}/questionnaires/${questionnaireUuid}/questions/${questionUuid}/options/${optionId}/delete-media`, {
        headers: {
            authorization: `Bearer ${getToken()}`
        }
    });
    return response.data;
};

// Public response API — used by QuestionnaireViewer and recording flow

export const startQuestionnaire = async (questionnaireUuid, data = {}) => {
    const response = await axios.post(`${API_BASE}/public/questionnaires/${questionnaireUuid}/start`, data);
    return response.data;
};

export const submitQuestionAnswer = async (responseUuid, data) => {
    const response = await axios.post(`${API_BASE}/public/questionnaires/responses/${responseUuid}/answer`, data);
    return response.data;
};

// Voice answers are uploaded on their own endpoint: the blob goes to storage and
// the backend queues transcription, which lands in the answer's text_response.
export const uploadVoiceAnswer = async (responseUuid, questionUuid, audioBlob, { sectionUuid = null, responseDuration = null } = {}) => {
    const formData = new FormData();
    formData.append('question_uuid', questionUuid);
    formData.append('audio', audioBlob, audioBlob.type.includes('mp4') ? 'answer.mp4' : 'answer.webm');
    if (sectionUuid) formData.append('section_uuid', sectionUuid);
    if (responseDuration != null) formData.append('response_duration', Math.round(responseDuration));

    const response = await axios.post(
        `${API_BASE}/public/questionnaires/responses/${responseUuid}/answer/voice`,
        formData
    );
    return response.data;
};

export const completeQuestionnaire = async (responseUuid) => {
    const response = await axios.post(`${API_BASE}/public/questionnaires/responses/${responseUuid}/complete`);
    return response.data;
};

export const getQuestionnaireResponses = async (projectUuid, questionnaireUuid) => {
    const response = await axios.get(
        `${API_BASE}/projects/${projectUuid}/questionnaires/${questionnaireUuid}/responses`,
        { headers: { authorization: `Bearer ${getToken()}` } }
    );
    return response.data;
};

// Individual answers for every participant (per-question / per-participant views)
export const getQuestionnaireAnswers = async (projectUuid, questionnaireUuid) => {
    const response = await axios.get(
        `${API_BASE}/projects/${projectUuid}/questionnaires/${questionnaireUuid}/answers`,
        { headers: { authorization: `Bearer ${getToken()}` } }
    );
    return response.data;
};

// Team-wide participant identifiers (subjects) and response attribution
export const getTeamParticipants = async (projectUuid, search = '') => {
    const response = await axios.get(`${API_BASE}/projects/${projectUuid}/participants`, {
        headers: { authorization: `Bearer ${getToken()}` },
        params: search ? { search } : {},
    });
    return response.data;
};

export const createTeamParticipant = async (projectUuid, participant) => {
    const response = await axios.post(`${API_BASE}/projects/${projectUuid}/participants`, participant, {
        headers: { authorization: `Bearer ${getToken()}` },
    });
    return response.data;
};

export const linkResponseParticipant = async (projectUuid, questionnaireUuid, responseUuid, participantUuid) => {
    const response = await axios.put(
        `${API_BASE}/projects/${projectUuid}/questionnaires/${questionnaireUuid}/responses/${responseUuid}/participant`,
        { participant_uuid: participantUuid },
        { headers: { authorization: `Bearer ${getToken()}` } }
    );
    return response.data;
};

export const getResponseRecordings = async (projectUuid, questionnaireUuid, responseUuid) => {
    const response = await axios.get(
        `${API_BASE}/projects/${projectUuid}/questionnaires/${questionnaireUuid}/responses/${responseUuid}/recordings`,
        { headers: { authorization: `Bearer ${getToken()}` } }
    );
    return response.data;
};
